import React from 'react';
import {Link} from 'react-router';
import {Anchor, Banner, Button, Text} from '@albert-io/atomic';

import CONSTANTS from 'client/constants';
import PracticeView from 'components/PracticeView/PracticeView.react';
import TitleBar from 'components/PracticeView/TitleBar/TitleBar.react';
import type {SubjectModelV2} from 'resources/augmented/Subject/SubjectModel.v2';

interface Props {
  subject: SubjectModelV2;
  subjectSlug: string;
}

/**
 * Shown on a direct question link (`/learn/question/...`) when the API returns no question for
 * it, for example after the question was removed or unpublished.
 */
const UnavailableQuestionSubjectPracticeView = ({subject, subjectSlug}: Props) => {
  const subjectLink = subject.isAdaptive()
    ? `/adaptive-subjects/${subjectSlug}`
    : `/${subjectSlug}`;
  const backgroundColor = `var(--color-background-domain-${subject.getCurriculumAreaSlug()})`;

  return (
    <PracticeView
      titleBar={
        <TitleBar
          backgroundColor={backgroundColor}
          title={subject.getName()}
          imgHref={subject.getLogoLocation().href}
          backButton={<TitleBar.BackButton as={Link} label='Back To Guide' to={subjectLink} />}
        />
      }
      stageComponent={
        <div className='flex justify-center'>
          <div
            className='m-x2 rounded-x1 border-border-primary background-primary p-x4 shadow-l3 max-w-prose border border-solid'
            data-testid='unavailable-question-subject-practice-view'
          >
            <Banner type='negative'>
              <Banner.Icon icon='exclamation-triangle' />
              <Text color='primary'>This question is no longer available.</Text>
            </Banner>
            <Text className='m-x2'>
              <p>It may have been removed or unpublished.</p>
              <p>
                If you expected to see it, ask your teacher or contact our support team at{' '}
                <Anchor href={`mailto:${CONSTANTS.CONTACT.EMAIL.SUPPORT}`}>
                  {CONSTANTS.CONTACT.EMAIL.SUPPORT}
                </Anchor>
                .
              </p>
            </Text>
            <div className='my-x2'>
              <Button
                as={Link}
                to={subjectLink}
                color='secondary'
                variant='outlined'
                size='s'
                data-testid='unavailable-question-subject-practice-view__back-btn'
              >
                Back to {subject.getName()}
              </Button>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default UnavailableQuestionSubjectPracticeView;
